import { Form, notification } from 'antd';
import type { TablePaginationConfig } from 'antd/es/table';
import type { SorterResult } from 'antd/es/table/interface';
import { localize, saveResponseFile } from 'mixins/helpers';
import {
    isCanceledRequest,
    useCancelableRequest,
} from 'mixins/hooks/useCancelableRequest';
import type { ChangeEvent } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
    getApiCallStatistics,
    getApiServiceDictionary,
    getApiCallStatisticsCsv,
} from '/react4xp/parts/admin-api-call-statistics/actions';
import {
    ApiCallStatistic,
    ApiCallStatisticsFilters,
    ApiServiceDictionaryItem,
} from '/react4xp/parts/admin-api-call-statistics/types';
import {
    API_DATE_FORMAT,
    DEFAULT_PAGE_SIZE,
    DEFAULT_SORT_FIELD,
    DEFAULT_SORT_ORDER,
} from '../utils/constants';
import {
    buildApiNamesMap,
    getRequestErrorKey,
    getSortParam,
    sanitizeTaxpayerCode,
} from '../utils/helpers';
import {
    CallStatisticsFilterValues,
    CallStatisticsSortState,
} from '../utils/types';

export const useCallStatistics = (translations: Record<string, string>) => {
    const [form] = Form.useForm<CallStatisticsFilterValues>();
    const {
        abortRequest: abortStatisticsRequest,
        createRequestSignal: createStatisticsRequestSignal,
        finishRequest: finishStatisticsRequest,
    } = useCancelableRequest();
    const [dictionary, setDictionary] = useState<ApiServiceDictionaryItem[]>([]);
    const [statistics, setStatistics] = useState<ApiCallStatistic[]>([]);
    const [filters, setFilters] = useState<ApiCallStatisticsFilters>({});
    const [hasSearched, setHasSearched] = useState(false);
    const [isDictionaryLoading, setIsDictionaryLoading] = useState(false);
    const [isExporting, setIsExporting] = useState(false);
    const [isStatisticsLoading, setIsStatisticsLoading] = useState(false);
    const [pagination, setPagination] = useState<TablePaginationConfig>({
        current: 1,
        pageSize: DEFAULT_PAGE_SIZE,
        total: 0,
    });
    const [sort, setSort] = useState<CallStatisticsSortState>({
        field: DEFAULT_SORT_FIELD,
        order: DEFAULT_SORT_ORDER,
    });

    const apiNames = useMemo(() => buildApiNamesMap(dictionary), [dictionary]);

    const notifyRequestError = useCallback(
        (error: any) => {
            notification.error({
                message: localize(getRequestErrorKey(error), translations),
            });
        },
        [translations]
    );

    const loadDictionary = useCallback(() => {
        setIsDictionaryLoading(true);

        getApiServiceDictionary()
            .then((response) => {
                setDictionary(response.data.filter((item) => item.isActive));
            })
            .catch(notifyRequestError)
            .finally(() => setIsDictionaryLoading(false));
    }, [notifyRequestError]);

    const loadStatistics = useCallback(() => {
        const signal = createStatisticsRequestSignal();
        setIsStatisticsLoading(true);

        getApiCallStatistics({
            ...filters,
            page: (pagination.current || 1) - 1,
            size: pagination.pageSize || DEFAULT_PAGE_SIZE,
            sort: getSortParam(sort),
        }, signal)
            .then((response) => {
                setStatistics(response.data.content || []);
                setPagination((current) => ({
                    ...current,
                    current: response.data.number + 1,
                    pageSize: response.data.size,
                    total: response.data.totalElements,
                }));
            })
            .catch((error) => {
                if (isCanceledRequest(error)) {
                    return;
                }
                setStatistics([]);
                setPagination((current) => ({
                    ...current,
                    total: 0,
                }));
                notifyRequestError(error);
            })
            .finally(() => {
                if (finishStatisticsRequest(signal)) {
                    setIsStatisticsLoading(false);
                }
            });
    }, [
        createStatisticsRequestSignal,
        finishStatisticsRequest,
        filters,
        notifyRequestError,
        pagination.current,
        pagination.pageSize,
        sort,
    ]);

    useEffect(() => {
        loadDictionary();
    }, [loadDictionary]);

    useEffect(() => {
        if (hasSearched) {
            loadStatistics();
        }
    }, [hasSearched, loadStatistics]);

    const handleSearch = (values: CallStatisticsFilterValues) => {
        const [dateFrom, dateTo] = values.period || [];

        setPagination((current) => ({
            ...current,
            current: 1,
        }));
        setFilters({
            taxpayerCode: values.taxpayerCode || undefined,
            apiCode: values.apiCode || undefined,
            dateFrom: dateFrom?.format(API_DATE_FORMAT),
            dateTo: dateTo?.format(API_DATE_FORMAT),
        });
        setHasSearched(true);
    };

    const handleReset = () => {
        abortStatisticsRequest();
        form.resetFields();
        setStatistics([]);
        setPagination((current) => ({
            ...current,
            current: 1,
            total: 0,
        }));
        setFilters({});
        setHasSearched(false);
        setIsStatisticsLoading(false);
        setSort({
            field: DEFAULT_SORT_FIELD,
            order: DEFAULT_SORT_ORDER,
        });
    };

    const handleTaxpayerCodeChange = (event: ChangeEvent<HTMLInputElement>) => {
        form.setFieldValue('taxpayerCode', sanitizeTaxpayerCode(event.target.value));
    };

    const handleExport = () => {
        if (!statistics.length) {
            return;
        }

        setIsExporting(true);

        getApiCallStatisticsCsv(filters)
            .then((response) => {
                saveResponseFile(response, 'api-billing-stat.csv');
                }).catch(notifyRequestError)
            .finally(() => setIsExporting(false));
    };

    const handleTableChange = (
        nextPagination: TablePaginationConfig,
        _tableFilters: Record<string, any>,
        nextSorter: SorterResult<ApiCallStatistic> | SorterResult<ApiCallStatistic>[]
    ) => {
        setPagination((current) => ({
            ...current,
            current: nextPagination.current || 1,
            pageSize: nextPagination.pageSize || DEFAULT_PAGE_SIZE,
        }));

        const activeSorter = Array.isArray(nextSorter) ? nextSorter[0] : nextSorter;

        if (activeSorter?.field && activeSorter.order) {
            setSort({
                field: String(activeSorter.field),
                order: activeSorter.order,
            });
        } else {
            setSort({
                field: DEFAULT_SORT_FIELD,
                order: DEFAULT_SORT_ORDER,
            });
        }
    };

    return {
        apiNames,
        dictionary,
        form,
        handleReset,
        handleSearch,
        handleExport,
        handleTableChange,
        handleTaxpayerCodeChange,
        isDictionaryLoading,
        isExporting,
        isStatisticsLoading,
        pagination,
        sort,
        statistics,
    };
};